import type { Address } from '@ahm/api-wrappers-core';
import { publicEnv } from '@/config/public-env.config.mjs';

// gw-maps gateway base — cùng biến với autocomplete và reverse geocode.
const MAPS_BASE_URL = publicEnv.NEXT_PUBLIC_EP_API_URI.replace(/\/+$/, '');

// Lưu ý: batch geocode nằm ở `/geo/...` (KHÔNG phải `/wgeo/...` như reverse).
const BATCH_GEOCODE_URL = `${MAPS_BASE_URL}/geo/batch_geocode`;

/** One entry of the gw-maps `/geo/batch_geocode` response (per input address). */
interface BatchGeocodeResult {
  formattedAddress?: string;
  short_address?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  county?: string;
  provider?: string;
  source?: string;
  isCached?: boolean;
}

/**
 * Forward-geocode an address string into an `Address` (lat/lng + display text)
 * via Ahamove's gw-maps `batch_geocode` endpoint.
 *
 * This is the single resolver used by every address-picker flow to turn a
 * selected/typed address (which has no coordinates) into one with lat/lng.
 * Replaces the legacy Google place-detail resolver.
 *
 * Returns `null` when the address is empty or no coordinates are found.
 */
export const getBatchGeocodeFn = async (
  address?: string
): Promise<Address | null> => {
  const query = address?.trim();
  if (!query) return null;

  try {
    const response = await fetch(BATCH_GEOCODE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        addresses: [{ address: query }],
        profile: 'partner',
      }),
    });

    if (!response.ok) return null;

    const results = (await response.json()) as BatchGeocodeResult[];
    const result = results?.[0];

    if (
      !result ||
      typeof result.latitude !== 'number' ||
      typeof result.longitude !== 'number'
    ) {
      return null;
    }

    const formatted = result.formattedAddress ?? result.short_address;

    return {
      // Keep the text the user actually selected as the primary address; fall
      // back to the geocoder's formatted address, then the raw query.
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      address: result.address || formatted || query,
      short_address: formatted,
      lat: result.latitude,
      lng: result.longitude,
    };
  } catch (error) {
    console.error('Error in getBatchGeocodeFn:', error);
    return null;
  }
};
